import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventItem } from '@/mocks/events';
import { formatDate } from '@/utils/news';

interface HeroCarouselProps {
  items: EventItem[];
}

const heroImages = [
  'https://readdy.ai/api/search-image?query=Vibrant%20livestream%20singing%20contest%20stage%20with%20warm%20orange%20spotlights%2C%20confetti%20and%20a%20young%20female%20host%20singing%20into%20a%20microphone%2C%20energetic%20modern%20event%20photography%2C%20clean%20dark%20stage%20background%20with%20soft%20bokeh&width=1600&height=900&seq=wldg-hero-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Dynamic%20street%20dance%20battle%20stage%20with%20a%20young%20dancer%20mid%20jump%2C%20dramatic%20orange%20and%20cyan%20lighting%2C%20crowd%20silhouettes%2C%20cinematic%20event%20photography%2C%20clean%20modern%20dark%20stage%20backdrop&width=1600&height=900&seq=wldg-hero-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Two%20young%20livestreamers%20competing%20in%20a%20lively%20PK%20battle%20with%20ring%20lights%20and%20neon%20orange%20glow%2C%20split%20screen%20studio%20setup%2C%20modern%20streaming%20scene%2C%20clean%20bright%20dark%20background&width=1600&height=900&seq=wldg-hero-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Cheerful%20young%20female%20livestream%20host%20selling%20products%20with%20hand%20gestures%2C%20professional%20studio%20with%20orange%20accents%2C%20bright%20ring%20lights%2C%20modern%20e-commerce%20live%20scene%2C%20clean%20background&width=1600&height=900&seq=wldg-hero-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Young%20talents%20performing%20on%20a%20bright%20variety%20show%20stage%20with%20orange%20gold%20lighting%2C%20audience%20and%20host%20in%20the%20frame%2C%20festive%20modern%20event%20photography%2C%20clean%20dark%20backdrop&width=1600&height=900&seq=wldg-hero-05&orientation=landscape',
];

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (next: number) => setIndex((next + count) % count);
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={heroImages[i % heroImages.length]}
            alt={`${item.title} 焦点图`}
            title={`${item.title} - 网红大赛焦点`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20"></div>
        </div>
      ))}

      <div className="relative h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-12 md:pb-16 w-full">
        <h1 className="font-heading font-bold text-primary-200 text-sm md:text-base tracking-wide mb-2">
          每日大瓜网红大赛百度 · 最新网红赛事资讯与动态
        </h1>
        <span className="inline-flex w-fit items-center gap-1 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-semibold mb-3">
          <i className="ri-fire-fill"></i>
          {active.categoryName}
        </span>
        <h2 className="font-heading font-extrabold text-2xl md:text-4xl lg:text-5xl text-white leading-tight max-w-3xl line-clamp-2">
          {active.title}
        </h2>
        <p className="mt-3 md:mt-4 text-sm md:text-base text-white/80 max-w-2xl line-clamp-2">
          {active.summary}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-white/70 text-xs md:text-sm">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {formatDate(active.date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-map-pin-line"></i>
            {active.region}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-user-star-line"></i>
            {active.host}
          </span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={`/detail/${active.id}`}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            阅读详情
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to={`/category/${active.categoryId}`}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-white/15 text-white text-sm font-semibold border border-white/30 hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            同分类赛事
          </Link>
        </div>

        <div className="mt-8 flex items-center gap-3">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-10 bg-primary-400' : 'w-5 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </section>
  );
}